import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { TrendingUp, ArrowLeft, Loader2, Globe, Mail, Phone, Gift, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import TurnstileWidget from '../components/TurnstileWidget';

export default function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, toggleLanguage, language } = useLanguage();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [authMethod, setAuthMethod] = useState('email');
  const [referralCode, setReferralCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileRequired = Boolean(process.env.REACT_APP_TURNSTILE_SITE_KEY);
  const turnstileRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    income_level: '',
    preferred_language: language
  });

  // Check for referral code in URL
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      setReferralCode(ref);
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.password) {
      toast.error(t('Please fill all required fields', 'Tafadhali jaza sehemu zote zinazohitajika'));
      return;
    }

    if (authMethod === 'email' && !formData.email) {
      toast.error(t('Please enter your email', 'Tafadhali ingiza barua pepe yako'));
      return;
    }

    if (authMethod === 'phone' && !formData.phone) {
      toast.error(t('Please enter your phone number', 'Tafadhali ingiza nambari ya simu yako'));
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error(t('Passwords do not match', 'Manenosiri hayalingani'));
      return;
    }

    if (formData.password.length < 6) {
      toast.error(t('Password must be at least 6 characters', 'Nenosiri lazima liwe na herufi 6 au zaidi'));
      return;
    }

    if (turnstileRequired && !turnstileToken) {
      toast.error(t('Please wait for the security check to finish',
                    'Tafadhali subiri uthibitisho wa usalama ukamilike'));
      turnstileRef.current?.reset();
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: formData.name,
        password: formData.password,
        preferred_language: language,
        income_level: formData.income_level || null,
        turnstileToken: turnstileToken,
      };

      if (authMethod === 'email') {
        payload.email = formData.email;
      } else {
        payload.phone = formData.phone;
      }

      // Include referral code if present
      if (referralCode) {
        payload.referral_code = referralCode;
      }

      await register(payload);
      if (referralCode) {
        toast.success(t('Account created with referral bonus! Welcome!', 'Akaunti imeundwa na bonasi ya rufaa! Karibu!'));
      } else {
        toast.success(t('Account created! Welcome to Wekeza-Smart', 'Akaunti imeundwa! Karibu Wekeza-Smart'));
      }
      // If the user arrived from a Premium CTA, drop them back on the landing
      // page with the pricing anchor so the subscribe dialog is one tap away.
      const intent = sessionStorage.getItem('wekeza_intent');
      if (intent === 'subscribe_premium') {
        sessionStorage.removeItem('wekeza_intent');
        navigate('/?upgrade=premium#pricing');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('Registration failed', 'Usajili umeshindikana'));
    } finally {
      setLoading(false);
      setTurnstileToken(null);
      turnstileRef.current?.reset();
    }
  };

  const incomeOptions = [
    { value: 'below_500k', label_en: 'Below TZS 500,000/month', label_sw: 'Chini ya TZS 500,000/mwezi' },
    { value: '500k_1m', label_en: 'TZS 500,000 - 1,000,000/month', label_sw: 'TZS 500,000 - 1,000,000/mwezi' },
    { value: '1m_3m', label_en: 'TZS 1,000,000 - 3,000,000/month', label_sw: 'TZS 1,000,000 - 3,000,000/mwezi' },
    { value: '3m_5m', label_en: 'TZS 3,000,000 - 5,000,000/month', label_sw: 'TZS 3,000,000 - 5,000,000/mwezi' },
    { value: 'above_5m', label_en: 'Above TZS 5,000,000/month', label_sw: 'Zaidi ya TZS 5,000,000/mwezi' }
  ];

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left side - Image (hidden on mobile) */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <div className="absolute inset-0 bg-primary/90" />
        <img 
          src="https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwyfHxpbnZlc3RtZW50JTIwZ3Jvd3RoJTIwcGxhbnQlMjBjb2luJTIwY29uY2VwdHxlbnwwfHx8fDE3NzA5ODY0NjB8MA&ixlib=rb-4.1.0&q=85"
          alt="Investment growth concept"
          className="absolute inset-0 w-full h-full object-cover mix-blend-overlay"
        />
        <div className="relative z-10 flex flex-col justify-center p-12 text-white">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
              <TrendingUp className="w-6 h-6" />
            </div>
            <span className="font-bold text-2xl">Wekeza-Smart</span>
          </div>
          <h1 className="text-4xl font-bold mb-4">
            {t('Start Your Journey', 'Anza Safari Yako')}
          </h1>
          <p className="text-white/80 text-lg max-w-md">
            {t(
              'Create your free account and start learning about investments in Tanzania. It takes less than 2 minutes.',
              'Tengeneza akaunti yako bure na uanze kujifunza kuhusu uwekezaji Tanzania. Inachukua chini ya dakika 2.'
            )}
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex flex-col">
        <header className="p-4 flex items-center justify-between">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => navigate('/')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('Back', 'Rudi')}
          </Button>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={toggleLanguage}
            className="flex items-center gap-2"
            data-testid="register-language-toggle"
          >
            <Globe className="w-4 h-4" />
            {language === 'en' ? 'Kiswahili' : 'English'}
          </Button>
        </header>

        <div className="flex-1 flex items-center justify-center p-6">
          <Card className="w-full max-w-md border-0 shadow-none lg:border lg:shadow-sm">
            <CardHeader className="text-center lg:text-left">
              <div className="lg:hidden flex items-center justify-center gap-2 mb-4">
                <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-primary-foreground" />
                </div>
                <span className="font-bold text-xl">Wekeza-Smart</span>
              </div>
              <CardTitle className="text-2xl">
                {t('Create Account', 'Tengeneza Akaunti')}
              </CardTitle>
              <CardDescription>
                {t('Sign up with your email or phone number', 'Jisajili na barua pepe au nambari ya simu')}
              </CardDescription>
              {referralCode && (
                <Badge className="mt-2 bg-emerald-500 hover:bg-emerald-600">
                  <Gift className="w-3 h-3 mr-1" />
                  {t('Referred by educator', 'Umependekezwa na mwalimu')}: {referralCode}
                </Badge>
              )}
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">
                    {t('Full Name', 'Jina Kamili')} *
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    name="name"
                    placeholder={t('e.g. John Mwalimu', 'mfano John Mwalimu')}
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="h-12"
                    data-testid="register-name-input"
                    autoComplete="name"
                  />
                </div>

                <Tabs value={authMethod} onValueChange={setAuthMethod} className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="email" className="flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      Email
                    </TabsTrigger>
                    <TabsTrigger value="phone" className="flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      {t('Phone', 'Simu')}
                    </TabsTrigger>
                  </TabsList>
                  <TabsContent value="email" className="mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">
                        {t('Email Address', 'Barua Pepe')} *
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="john@example.com"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="h-12"
                        data-testid="register-email-input"
                      />
                    </div>
                  </TabsContent>
                  <TabsContent value="phone" className="mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="phone">
                        {t('Phone Number', 'Nambari ya Simu')} *
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="0712345678"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        className="h-12"
                        data-testid="register-phone-input"
                      />
                    </div>
                  </TabsContent>
                </Tabs>

                <div className="space-y-2">
                  <Label htmlFor="income">
                    {t('Monthly Income (Optional)', 'Mapato ya Kila Mwezi (Si lazima)')}
                  </Label>
                  <Select 
                    value={formData.income_level} 
                    onValueChange={(value) => setFormData({...formData, income_level: value})}
                  >
                    <SelectTrigger className="h-12" data-testid="register-income-select">
                      <SelectValue placeholder={t('Select income range', 'Chagua kiwango cha mapato')} />
                    </SelectTrigger>
                    <SelectContent>
                      {incomeOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {t(option.label_en, option.label_sw)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">
                    {t('Password', 'Nenosiri')} *
                  </Label>
                  <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="h-12 pr-12"
                    data-testid="register-password-input"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground focus:outline-none"
                    aria-label={showPassword ? t('Hide password', 'Ficha nenosiri') : t('Show password', 'Onyesha nenosiri')}
                    data-testid="register-toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">
                    {t('Confirm Password', 'Thibitisha Nenosiri')} *
                  </Label>
                  <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    placeholder="••••••••"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                    className="h-12 pr-12"
                    data-testid="register-confirm-password-input"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground focus:outline-none"
                    aria-label={showConfirmPassword ? t('Hide password', 'Ficha nenosiri') : t('Show password', 'Onyesha nenosiri')}
                    data-testid="register-toggle-confirm-password-visibility"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-12 rounded-full text-base btn-press"
                  disabled={loading}
                  data-testid="register-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t('Creating account...', 'Inaunda akaunti...')}
                    </>
                  ) : (
                    t('Create Account', 'Tengeneza Akaunti')
                  )}
                </Button>
                <TurnstileWidget ref={turnstileRef} action="register" onToken={setTurnstileToken} />
              </form>
              <div className="mt-6 text-center">
                <p className="text-sm text-muted-foreground">
                  {t("Already have an account?", "Tayari una akaunti?")}{' '}
                  <Link 
                    to="/login" 
                    className="text-primary font-medium hover:underline"
                    data-testid="register-login-link"
                  >
                    {t('Sign in', 'Ingia')}
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
